import React, { useState, useCallback } from 'react';
import { Divider } from 'theme/Global.styles';
import { Grid, Row, Col } from '@zendeskgarden/react-grid';

import { variables } from 'theme/variables';
import { PropTypes } from 'prop-types';
import { TabList, Tab } from '@zendeskgarden/react-tabs';
import { productNominalDetailsShape } from 'propTypesObjects';
import { IconButton } from '@zendeskgarden/react-buttons';
import Icon from 'components/Icon/Icon';
import { findIndex } from 'lodash';
import {
  PipeFactsTechnicalDetails,
  PipeFactsTechnicalDetailsTab,
  PipeFactsSectionHeader,
} from './ProductsPipeFacts.styles';
import {
  QualityInfoTab,
  SizeStrengthTab,
  AlloyDetailsTab,
  ConnectionDetails,
} from './components/TechnicalDetailsTabs';

const { color_grey_400: colorGrey400, color_white: colorWhite } = variables;

function ProductsPipeFacts({ productDetails, isProductPage, onInquire }) {
  const [currentTab, setCurrentTab] = useState(
    isProductPage || productDetails?.status !== 'active'
      ? 'sizeStrength'
      : 'qualityInfo'
  );

  const changeTab = useCallback(
    (direction) => {
      const tabArr =
        isProductPage || productDetails?.status !== 'active'
          ? ['sizeStrength', 'alloyDetails', 'connectionDetails']
          : [
              'qualityInfo',
              'sizeStrength',
              'alloyDetails',
              'connectionDetails',
            ];
      if (direction === 'back') {
        const currentTabIdx = findIndex(tabArr, (tab) => tab === currentTab);
        setCurrentTab(tabArr[currentTabIdx - 1]);
      }
      if (direction === 'forward') {
        const currentTabIdx = findIndex(tabArr, (tab) => tab === currentTab);
        setCurrentTab(tabArr[currentTabIdx + 1]);
      }
    },
    [currentTab, isProductPage, productDetails?.status]
  );

  const renderTab = useCallback(
    (tabName, prodDetails) => {
      switch (tabName) {
        case 'qualityInfo':
          return (
            <QualityInfoTab
              productDetails={prodDetails}
              listingsRefactor
              onInquire={onInquire}
            />
          );
        case 'sizeStrength':
          return (
            <SizeStrengthTab productDetails={prodDetails} listingsRefactor />
          );
        case 'alloyDetails':
          return (
            <AlloyDetailsTab productDetails={prodDetails} listingsRefactor />
          );
        case 'connectionDetails':
          return (
            <ConnectionDetails productDetails={prodDetails} listingsRefactor />
          );
        default:
          return (
            <QualityInfoTab
              productDetails={prodDetails}
              listingsRefactor
              onInquire={onInquire}
            />
          );
      }
    },
    [onInquire]
  );

  const renderProductsPipeFacts = useCallback(
    () => (
      <PipeFactsTechnicalDetails>
        <Grid>
          <Row className="titleAndTabs">
            <Col lg={3}>
              <PipeFactsSectionHeader>
                {`${
                  isProductPage || productDetails?.status !== 'active'
                    ? ''
                    : 'Pipe Body'
                } Technical Details`}
              </PipeFactsSectionHeader>
            </Col>
            <Col className="tabListCol">
              <TabList className="tabs no-border" style={{ marginTop: '18px' }}>
                {(productDetails?.status === 'active' ||
                  (!isProductPage && productDetails?.status === 'active')) && (
                  <Tab
                    className="tab"
                    slate
                    onClick={() => {
                      setCurrentTab('qualityInfo');
                    }}
                    selected={currentTab === 'qualityInfo'}
                  >
                    Quality Information
                  </Tab>
                )}
                <Tab
                  className="tab"
                  slate
                  onClick={() => {
                    setCurrentTab('sizeStrength');
                  }}
                  selected={currentTab === 'sizeStrength'}
                >
                  Size & Strength
                </Tab>
                <Tab
                  className="tab"
                  slate
                  onClick={() => {
                    setCurrentTab('alloyDetails');
                  }}
                  selected={currentTab === 'alloyDetails'}
                >
                  Alloy Details
                </Tab>
                <Tab
                  className="tab"
                  slate
                  onClick={() => {
                    setCurrentTab('connectionDetails');
                  }}
                  selected={currentTab === 'connectionDetails'}
                >
                  Connection Details
                </Tab>
              </TabList>
            </Col>
          </Row>
          <Divider
            horizontal
            background={colorGrey400}
            style={{ marginBottom: '10px' }}
          />
          <Row className="tabData">
            {((!isProductPage && currentTab !== 'qualityInfo') ||
              (isProductPage && currentTab !== 'sizeStrength')) && (
              <IconButton
                size="small"
                className="directionButton"
                id="prevTab"
                onClick={() => changeTab('back')}
              >
                <Icon
                  button
                  icon="icon-chevron-left"
                  fontSize="12px"
                  style={{ padding: 0, color: colorWhite }}
                />
              </IconButton>
            )}
            <PipeFactsTechnicalDetailsTab>
              {renderTab(currentTab, productDetails)}
            </PipeFactsTechnicalDetailsTab>
            {currentTab !== 'connectionDetails' && (
              <IconButton
                size="small"
                className="directionButton"
                id="nextTab"
                onClick={() => changeTab('forward')}
              >
                <Icon
                  button
                  icon="icon-chevron-right"
                  fontSize="12px"
                  style={{ padding: 0, color: colorWhite }}
                />
              </IconButton>
            )}
          </Row>
        </Grid>
      </PipeFactsTechnicalDetails>
    ),
    [currentTab, renderTab, productDetails, isProductPage, changeTab]
  );

  return renderProductsPipeFacts();
}

ProductsPipeFacts.propTypes = {
  active: PropTypes.string,
  productDetails: productNominalDetailsShape,
  isProductPage: PropTypes.bool,
  onInquire: PropTypes.func,
};

export default ProductsPipeFacts;
